import React, { useState, useRef } from 'react';
import { useImportOrganizationSettings } from '../../hooks/use-organization';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { OrganizationSettingsExport, OrganizationSettingsImportRequest, OrganizationSettingsImportResult } from '../../types';
import { Button } from '../ui/button';
import { Upload, CheckCircle, AlertCircle, FileJson } from 'lucide-react';

interface OrganizationSettingsImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'upload' | 'preview' | 'result';

export function OrganizationSettingsImportModal({
  isOpen,
  onClose,
}: OrganizationSettingsImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [exported, setExported] = useState<OrganizationSettingsExport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importSecurity, setImportSecurity] = useState(true);
  const [importAuthentication, setImportAuthentication] = useState(true);
  const [importIntegrations, setImportIntegrations] = useState(true);
  const [importResult, setImportResult] = useState<OrganizationSettingsImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useImportOrganizationSettings();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        if (!parsed.schemaVersion || !parsed.security || !parsed.authentication || !parsed.integrations) {
          setParseError('This file does not appear to be a valid organization settings export.');
          return;
        }
        setExported(parsed as OrganizationSettingsExport);
        setStep('preview');
      } catch {
        setParseError('Failed to parse JSON file. Please make sure it is a valid organization settings export.');
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!exported) return;

    const request: OrganizationSettingsImportRequest = {
      settings: exported,
      importSecurity,
      importAuthentication,
      importIntegrations,
    };

    try {
      const result = await importMutation.mutateAsync(request);
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setParseError(err?.response?.data?.message || 'An error occurred during import.');
    }
  };

  const handleClose = () => {
    setStep('upload');
    setExported(null);
    setParseError(null);
    setImportResult(null);
    setImportSecurity(true);
    setImportAuthentication(true);
    setImportIntegrations(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-settings-import-modal-title"
          tabIndex={-1}
          className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-lg mx-auto z-10 focus:outline-none"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <h3 id="org-settings-import-modal-title" className="text-lg font-semibold text-secondary-900 dark:text-white">
              Import Organization Settings
            </h3>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              Restore settings from a previously exported file
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Step: Upload */}
            {step === 'upload' && (
              <>
                <div
                  className="flex flex-col items-center justify-center border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-10 cursor-pointer hover:border-primary-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileJson className="h-10 w-10 text-secondary-400 mb-3" />
                  <p className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Click to select a settings file
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">.json files only</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={handleFileChange}
                />
                {parseError && (
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{parseError}</span>
                  </div>
                )}
              </>
            )}

            {/* Step: Preview */}
            {step === 'preview' && exported && (
              <>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-md border border-secondary-200 dark:border-secondary-700 p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Source organization</span>
                    <span className="font-medium text-secondary-900 dark:text-white">{exported.organizationName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Exported at</span>
                    <span className="text-secondary-700 dark:text-secondary-200">
                      {new Date(exported.exportedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Schema version</span>
                    <span className="text-secondary-700 dark:text-secondary-200">{exported.schemaVersion}</span>
                  </div>
                </div>

                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium text-secondary-700 dark:text-secondary-200">What to import:</legend>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importSecurity}
                      onChange={(e) => setImportSecurity(e.target.checked)}
                      className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Security settings
                      <span className="text-secondary-500 dark:text-secondary-400 ml-1">— password policies, lockout rules</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importAuthentication}
                      onChange={(e) => setImportAuthentication(e.target.checked)}
                      className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Authentication settings
                      <span className="text-secondary-500 dark:text-secondary-400 ml-1">— SSO, session configuration</span>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importIntegrations}
                      onChange={(e) => setImportIntegrations(e.target.checked)}
                      className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-200">
                      Integration settings
                      <span className="text-secondary-500 dark:text-secondary-400 ml-1">— SMTP, API keys (passwords preserved from current config)</span>
                    </span>
                  </label>
                </fieldset>

                {importIntegrations && (
                  <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      SMTP passwords are never included in exports — the existing password in the current configuration will be preserved.
                    </span>
                  </div>
                )}

                {parseError && (
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{parseError}</span>
                  </div>
                )}
              </>
            )}

            {/* Step: Result */}
            {step === 'result' && importResult && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Import completed successfully</span>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-md border border-secondary-200 dark:border-secondary-700 p-4 space-y-2 text-sm">
                  {importResult.securityUpdated && (
                    <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-200">
                      <span className="w-3 h-3 rounded-full bg-primary-500 inline-block" />
                      Security settings updated
                    </div>
                  )}
                  {importResult.authenticationUpdated && (
                    <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-200">
                      <span className="w-3 h-3 rounded-full bg-primary-500 inline-block" />
                      Authentication settings updated
                    </div>
                  )}
                  {importResult.integrationsUpdated && (
                    <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-200">
                      <span className="w-3 h-3 rounded-full bg-primary-500 inline-block" />
                      Integration settings updated
                    </div>
                  )}
                </div>
                {importResult.warnings.length > 0 && (
                  <div className="space-y-1">
                    {importResult.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md p-3 text-sm">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end gap-3">
            {step === 'upload' && (
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            )}
            {step === 'preview' && (
              <>
                <Button variant="outline" onClick={() => { setStep('upload'); setParseError(null); }}>
                  Back
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importMutation.isPending || (!importSecurity && !importAuthentication && !importIntegrations)}
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  {importMutation.isPending ? 'Importing…' : 'Import Settings'}
                </Button>
              </>
            )}
            {step === 'result' && (
              <Button onClick={handleClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
